import React, { useState, useRef, useEffect } from "react";
import Player from "@vimeo/player";
import {
  Minus, Activity, ChevronLeft, ChevronUp, ChevronDown, X, Plus, Phone, Check, Video,
  Play, Pause, UndoDot, RedoDot, Maximize, CircleGauge, Volume1, MessagesSquare, MonitorPlay, BookOpenCheck,
  TabletSmartphone, NotebookPen, BookDown, BookA, GalleryHorizontal, BadgePlus, Timer, AudioLines,
  FolderHeart, Forward, Earth, ListChecks, Gift, Aperture, BetweenHorizontalStart,
  ImagePlay, Film, NotepadText, AlarmSmoke, BellElectric, CircleCheck, LayoutGrid, CornerDownRight, BotMessageSquare
} from 'lucide-react';


const VimeoDefaultPlayer= ({ videoSettings, videoId, transcript, minHeight = 360, maxHeight = 720 }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hoverTime, setHoverTime] = useState(null);
  const [videoTime, setVideoTime] = useState(0);
  const [volume, setVolume] = useState(1); // Vimeo uses 0..1
  const [speed, setSpeed] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [hoverVideo, setHoverVideo] = useState(true);
  const [speedShow, setSpeedShow] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper: extract numeric Vimeo ID from URL / iframe / direct id
  function getVimeoId(input) {
    try {
      if (!input) return null;
      const str = String(input).trim();
      // If it's an iframe tag, extract src
      if (str.startsWith('<iframe')) {
        const srcMatch = str.match(/src="([^"]+)"/);
        if (srcMatch) {
          const url = srcMatch[1];
          const idMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
          return idMatch ? idMatch[1] : null;
        }
      }
      // If it's a url
      const urlMatch = str.match(/vimeo\.com\/(?:video\/)?(\d+)/) || str.match(/^(\d+)$/);
      if (urlMatch) return urlMatch[1];
      // last resort: try to find digits
      const digits = str.match(/(\d{6,})/);
      return digits ? digits[1] : null;
    } catch (e) {
      return null;
    }
  }

  // Best-effort thumbnail URLs for Vimeo: vumbnail.com provides quick thumbnails for many Vimeo videos.
  // It's not official Vimeo API, but widely used for previews. If it doesn't exist for a video, the img may 404.
  function getVimeoThumbnail(input) {
    const id = getVimeoId(input);
    if (!id) return null;
    return {
      small: `https://vumbnail.com/${id}.jpg`,
      medium: `https://vumbnail.com/${id}.jpg`,
      large: `https://vumbnail.com/${id}.jpg`,
    };
  }

  // Initialize Vimeo player when iframe mounts or videoId changes
  useEffect(() => {
    const id = getVimeoId(videoId);
    if (!id) return;

    // If an existing player exists, destroy it first
    if (playerRef.current) {
      try { playerRef.current.unload(); } catch (e) { /* ignore */ }
      playerRef.current = null;
    }

    // Create an iframe element for Vimeo
    let iframe = iframeRef.current;
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframeRef.current = iframe;
    }

    const src = `https://player.vimeo.com/video/${id}?api=1&player_id=vimeo-player-${id}`;
    iframe.setAttribute('src', src);
    iframe.setAttribute('width', '100%');
    iframe.setAttribute('height', isFullScreen ? String(screenHeight) : String(minHeight));
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');

    // attach to container
    const mountPoint = containerRef.current?.querySelector('.vimeo-iframe-mount');
    if (mountPoint) {
      mountPoint.innerHTML = '';
      mountPoint.appendChild(iframe);
    }

    // instantiate player
    const player = new Player(iframe);
    playerRef.current = player;

    // promises to set initial state
    player.getDuration().then(d => setDuration(d)).catch(() => {});
    player.getPaused().then(p => setIsPlaying(!p)).catch(() => {});
    player.getVolume().then(v => setVolume(v)).catch(() => {});
    player.getPlaybackRate().then(r => setSpeed(r)).catch(() => {});

    // events
    const onPlay = () => {
      setIsPlaying(true);
      // start interval to update time
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(async () => {
        try {
          const current = await player.getCurrentTime();
          setVideoTime(current);
          
          if (duration) setProgress((current / duration) * 100);
        } catch (e) {}
      }, 500);
    };

    const onPause = () => {
      setIsPlaying(false);
      clearInterval(intervalRef.current);
    };

   const onTimeUpdate = (data) => {
  // data can be an object like { seconds: 12.34, duration: 120 } or a raw number
  const current = (data && typeof data === 'object') ? (data.seconds ?? Number(data)) : Number(data);

  // if the event includes duration, update state so we stay in sync
  if (data && typeof data === 'object' && data.duration != null) {
    setDuration(Number(data.duration));
  }

  // choose the best duration: prefer event duration if provided, otherwise fall back to component state
  const d = (data && typeof data === 'object' && data.duration != null) ? Number(data.duration) : (duration || 0);

  // avoid divide-by-zero and clamp between 0 and 100
  const pct = d > 0 ? (current / d) * 100 : 0;
  setProgress(Math.max(0, Math.min(100, pct)));

  setVideoTime(current);
};

    const onLoaded = () => {
      player.getDuration().then(d => setDuration(d)).catch(() => {});
    };

    player.on('play', onPlay);
    player.on('pause', onPause);
    player.on('timeupdate', onTimeUpdate);
    player.on('loaded', onLoaded);

    // cleanup on unmount or id change
    return () => {
      clearInterval(intervalRef.current);
      try {
        player.off('play', onPlay);
        player.off('pause', onPause);
        player.off('timeupdate', onTimeUpdate);
        player.off('loaded', onLoaded);
        player.unload();
      } catch (e) { }
      playerRef.current = null;
    };
  }, [videoId, isFullScreen, screenHeight, minHeight]);

  // Controls
  const handlePlayPause = async () => {
    const player = playerRef.current;
    if (!player) return;
    try {
      const paused = await player.getPaused();
      if (!paused) {
        await player.pause();
        setIsPlaying(false); 
      } else {
        await player.play();
        // setIsPlaying(true) will be done by event
      }
    } catch (e) {
      console.warn('play/pause error', e);
    }
  };

  const handleSkip = async (seconds) => {
    try {
      const player = playerRef.current;
      if (!player) return;
      const current = await player.getCurrentTime();
      const newTime = Math.max(0, Math.min(duration, current + seconds));
      await player.setCurrentTime(newTime);
      setVideoTime(newTime);
      setProgress((newTime / duration) * 100);
    } catch (e) {
      console.warn('skip error', e);
    }
  };

  
  const handleTimeUpdate = () => {
    const currentTime = playerRef.current.getCurrentTime();
    setProgress((currentTime / duration) * 100);
    setVideoTime(currentTime);
  };


  const handleSliderChange = async (e) => {
    const newProgress = Number(e.target.value);
    const newTime = (newProgress / 100) * duration;
    try {
      await playerRef.current.setCurrentTime(newTime);
      setProgress(newProgress);
      setVideoTime(newTime);
    } catch (e) {}
  };

  const handleMouseMove = (e) => {
    const sliderRect = sliderRef.current?.getBoundingClientRect();
    if (!sliderRect || !duration) return;
    const hoverPosition = e.clientX - sliderRect.left;
    const hoverPercentage = Math.max(0, Math.min(1, hoverPosition / sliderRect.width));
    const ht = hoverPercentage * duration;
    setHoverTime(ht);
  };
  const handleMouseLeave = () => setHoverTime(null);

  const handleVolumeChange = async (e) => {
    const newVal = Number(e.target.value) / 100; // control gives 0..100
    setVolume(newVal);
    try { await playerRef.current.setVolume(newVal); } catch (e) { }
  };

  const handleSpeedChange = async (newSpeed) => {
    setSpeed(Number(newSpeed));
    try { await playerRef.current.setPlaybackRate(Number(newSpeed)); } catch (e) { }
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (containerRef?.current?.requestFullscreen) containerRef.current.requestFullscreen();
      else if (containerRef?.current?.webkitRequestFullscreen) containerRef.current.webkitRequestFullscreen();
      setIsFullScreen(true);
    } else {
      if (document?.exitFullscreen) document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  const formatTime = (seconds = 0) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs > 0 ? `${hrs}:` : ""}${String(mins).padStart(2, '0')}:${String(secs).padStart(2,'0')}`;
  };
const outerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height:  isFullScreen ? screenHeight-60 : minHeight+30, 
    width: "100%", 
    position: "relative", 
    backgroundColor: "transparent", 
  };
 const innerStyleTwo= {
  position:"relative",
    border:"7px solid rgba(256, 256, 256, 0.50302)", 
    color: "white",
    padding: "20px",borderRadius:`${videoSettings?.videoPlayButton?.playButtonBorderRadius?.value}px`,
    width: "50px", 
    height: "50px",
   
  };
  const innerStyle = { display: "flex",
    justifyContent: "center",
    alignItems: "center",textAlign: "center",cursor:"pointer",
    padding: "20px",borderRadius:`${videoSettings?.videoPlayButton?.playButtonBorderRadius?.value}px`,
    width: "50px",
    height: "50px", 
    background: `${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.color:"#222222"}`, 
    
  };
  return (
    <div ref={containerRef} style={{ width: '100%', margin: '0 auto', overflow: 'hidden', position: 'relative', minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px` }}>

      {!isPlaying ? (
        <div align="center" style={{ position: 'absolute', top:0, bottom:0, left:0, right:0, zIndex: 99999999, background: !videoSettings?.videoThumbnail?.hideThumbnail ? '#000' : 'transparent' }}>
          {!videoSettings?.videoThumbnail?.hideThumbnail ? (
            videoSettings?.vimeo?.showDefaultThumbnail ? (
              <img src={getVimeoThumbnail(videoId)?.large} alt="vimeo-thumb" style={{ height: isFullScreen ? screenHeight : '', width: isFullScreen ? '' : '100%' }} />
            ) : (
              <img src={videoSettings?.videoThumbnail?.src} alt="custom-thumb" style={{ height: isFullScreen ? screenHeight : '', width: isFullScreen ? '' : '100%' }} />
            )
          ) : (
            <div style={{ height: isFullScreen ? screenHeight : '', width: isFullScreen ? '' : '100%' }} />
          )}

          <div style={{ position: 'absolute', height: '70px', background: 'transparent', top: '-12%', bottom: 0, left: 0, right: 0 }}>
            <div style={outerStyle}>
             {duration?<div onClick={handlePlayPause}  style={innerStyle}> 
                   <div className="innerStyleTwoPlayOuter" style={innerStyleTwo}> 
                   <div className="innerStyleTwoPlay" ></div>
                    <Play strokeWidth={3}  size={45} style={{margin:"4px"}}  color="#fff"/>
                       </div>
                       </div>:<div   style={innerStyle}> 
                   <div  style={innerStyleTwo}> 
                   <div className="innerStyleTwoPlay" ></div>
                    <Play strokeWidth={3}  size={45} style={{margin:"4px"}}  color="#fff"/>
                       </div>
                       </div>}

            <div style={{position:"absolute",height:"79px",background:"#222",
                      width:"177px",top:"70%",borderRadius:"25px",padding:"0px"}}>
               <div style={{display: "flex",
                gap: "1px", justifyContent: "center", alignItems: "center" ,padding:"10px"}}><Timer strokeWidth={3}  size={20}   color="#fff"/>
                <span style={{fontSize:"18px",fontWeight:"900",color:"#fff",paddingLeft:"15px"}}> <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>{speed}</span>x </span> 
             </div>  
             <div style={{position:"absolute",height:"39px",background:"#333",
                      width:"94%",borderBottomLeftRadius:"25px",borderBottomRightRadius:"25px",borderTopLeftRadius:"5px"
                      ,borderTopRightRadius:"5px"
                      ,padding:"0px",marginLeft:"5px",marginRight:"5px",marginBottom:"5px",bottom:"0px"}}>
             <div style={{display: "flex",
                gap: "1px", justifyContent: "center", alignItems: "center" ,padding:"10px"}}>
                   <span style={{fontSize:"16px",fontWeight:"900",color:"#fff",paddingLeft:"15px"}}> 
                   {duration?<span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}> {videoTime?formatTime(videoTime)+" / ":"Duration: "} </span> :""} {duration?formatTime(duration):"Video is Not Ready."} </span> 
             </div> 
                      </div>
                      </div>

            </div>
          </div>

        </div>
      ) : null}

      <div className="vimeo-iframe-mount" style={{ width: '100%', height: isFullScreen ? screenHeight : minHeight }} />

      

    <style>{`
        .slider-container { width: 99%; margin-top: -11px !important; text-align: center; }
        .slider-container-volume { width: 90px; margin-top: 10px; text-align: center; }
        .btnPlayerCircleHover{ border-radius:50%; width:35px; height:35px; border:2px solid transparent }
         .innerStyleTwoPlayOuter:hover{background:${videoSettings?.videoPlayButton?.playButtonColor?.hover?.color?videoSettings?.videoPlayButton?.playButtonColor?.hover?.color:"#fff"} !important; }
.innerStyleTwoPlay{animation: PlayButtonAnim 2.8s infinite linear;
  border:7px solid rgba(256, 256, 256, 0.50302);
  position: absolute;top:0px;left:0px;right:0px;bottom: 0px;
  padding: 20px;border-radius:${videoSettings?.videoPlayButton?.playButtonBorderRadius?.value}px;
  width: 35px; 
  height: 35px;}
@keyframes PlayButtonAnim{
  0% {
      /* opacity: 0; */
      transform: scale(1.4)
  }
  50% {
      transform: scale(0.81)
  }
 
  100% {
      transform: scale(1.4)
  }
  }

        .btnPlayerCircleHover:hover{ border-radius:50%; background:#222; border:2px solid transparent }
        .btnPlayerSquareHover{ border-radius:25%; border:2px solid transparent }
        .btnPlayerSquareHover:hover{ border-radius:25%; background:#222; border:2px solid transparent }
         .innerStyleTwoPlayOuter:hover{background:${videoSettings?.videoPlayButton?.playButtonColor?.hover?.color?videoSettings?.videoPlayButton?.playButtonColor?.hover?.color:"#fff"} !important; }

      
        .video-player-custom-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; background: linear-gradient(90deg, ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color || '#222222'}, ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color || '#222222'}); border-radius: 5px; outline: none; cursor: pointer; }
        .video-player-custom-slider-volume { -webkit-appearance: none; appearance: none; width: 100%; height: 9px; background: linear-gradient(90deg, #f1f1f1, #fff); border-radius: 5px; outline: none; cursor: pointer; }
        .video-player-custom-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 20px; height: 20px; background: ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color || '#222222'}; border-radius: 50%; border: 2px solid #fff; cursor: pointer; box-shadow: 0 2px 5px rgba(0,0,0,0.3); }
        .video-player-custom-slider-volume::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 20px; height: 20px; background: ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color || '#222222'}; border-radius: 50%; border: 2px solid #fff; cursor: pointer; box-shadow: 0 2px 5px rgba(0,0,0,0.3); }
        @media (max-width: 767px) { .hideMessageBtnnXs{ display: none !important; } }
      `}</style>

    </div>
  );
};

export default VimeoDefaultPlayer;
